import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { type ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { EFontStyle, ELineStyle, type ESize, InputBorderEnum } from '@common/enums';
import {
    CatalogCategoriesView,
    type FilterFormType,
    type FilterPriceFormType,
    type FontStyleFormControl,
} from '@web/widgets/catalog-products/type';
import { CommonModule } from '@angular/common';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { type FormArray, FormBuilder } from '@angular/forms';

@Component({
    selector: 'web-filter-price-settings',
    templateUrl: './filter-price-settings.component.html',
    styleUrls: ['./filter-price-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, CommonElementsModule],
})
export class FilterPriceSettingsComponent {
    @Input() priceFormGroup: FilterFormType['price'];
    @Input() catalogView: CatalogCategoriesView;

    private readonly themeState = inject(ThemeStateService);
    protected readonly fb: FormBuilder = inject(FormBuilder);

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public readonly lineStyle = ELineStyle;

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.priceFormGroup.markAsTouched({ onlySelf: true });

        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setBorderRadius(value: number): void {
        this.priceFormGroup.markAsTouched({ onlySelf: true });

        this.borderRadius.patchValue(value);
    }

    public setBorderColor({ value }: ColorPickerEventPayload): void {
        this.borderColor.patchValue(value);
    }

    public isActiveTextStyle(fontStyle: EFontStyle): boolean {
        return this.priceLabelFontFormGroup.get('fontStyle').value.includes(fontStyle);
    }

    public setFontSize(value: number) {
        this.priceLabelFontFormGroup.get('fontSize').patchValue(value);
    }

    public toggleActiveTextStyle(fontStyle: EFontStyle): void {
        this.setFontStyle(this.priceLabelFontFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>, fontStyle);
    }

    public setLabelTextColor({ value }: ColorPickerEventPayload): void {
        this.priceLabelFontFormGroup.get('color').patchValue(value);
    }

    public setTextColor({ value }: ColorPickerEventPayload): void {
        this.priceFormGroup.markAsTouched({ onlySelf: true });
        this.textColor.patchValue(value);
    }

    private setFontStyle(control: FormArray<FontStyleFormControl>, fontStyle: EFontStyle) {
        const index = control.value.findIndex((el) => el === fontStyle);

        if (index > -1) {
            control.removeAt(index);
        } else {
            control.push(this.fb.control(fontStyle));
        }
    }

    public setBorderStyle(value: string) {
        this.borderStyle.patchValue(value as InputBorderEnum);
    }

    public setBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setColorLine({ value, presetName }: ColorPickerEventPayload): void {
        this.colorLine.patchValue(presetName ?? value);
    }

    public setColorFilledLine({ value, presetName }: ColorPickerEventPayload): void {
        this.colorFilledLine.patchValue(presetName ?? value);
    }

    public setColorButtons({ value, presetName }: ColorPickerEventPayload): void {
        this.colorButtons.patchValue(presetName ?? value);
    }

    public setInputSize(value: string): void {
        this.priceFormGroup.markAsTouched({ onlySelf: true });
        this.inputSize.patchValue(value as ESize);
    }

    public setEnabledSlider(value: boolean) {
        this.slider.patchValue(value);
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get SIZE_MIN(): number {
        return CSS_RESTRICTIONS.SIZE_MIN;
    }

    public get SIZE_MAX(): number {
        return CSS_RESTRICTIONS.SIZE_MAX;
    }

    public get FONT_SIZE_MIN(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MIN;
    }

    public get FONT_SIZE_MAX(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MAX;
    }

    public get label(): FilterPriceFormType['label'] {
        return this.priceFormGroup.get('label') as FilterPriceFormType['label'];
    }

    public get priceLabelFontFormGroup(): FilterPriceFormType['priceLabelFont'] {
        return this.priceFormGroup.get('priceLabelFont') as FilterPriceFormType['priceLabelFont'];
    }

    public get borderStyle(): FilterPriceFormType['borderStyle'] {
        return this.priceFormGroup.get('borderStyle') as FilterPriceFormType['borderStyle'];
    }

    public get textColor(): FilterPriceFormType['textColor'] {
        return this.priceFormGroup.get('textColor') as FilterPriceFormType['textColor'];
    }

    public get backgroundColor(): FilterPriceFormType['backgroundColor'] {
        return this.priceFormGroup.get('backgroundColor') as FilterPriceFormType['backgroundColor'];
    }

    public get borderColor(): FilterPriceFormType['borderColor'] {
        return this.priceFormGroup.get('borderColor') as FilterPriceFormType['borderColor'];
    }

    public get borderRadius(): FilterPriceFormType['borderRadius'] {
        return this.priceFormGroup.get('borderRadius') as FilterPriceFormType['borderRadius'];
    }

    public get inputSize(): FilterPriceFormType['inputSize'] {
        return this.priceFormGroup.get('inputSize') as FilterPriceFormType['inputSize'];
    }

    public get slider(): FilterPriceFormType['slider'] {
        return this.priceFormGroup.get('slider') as FilterPriceFormType['slider'];
    }

    public get colorLine(): FilterPriceFormType['colorLine'] {
        return this.priceFormGroup.get('colorLine') as FilterPriceFormType['colorLine'];
    }

    public get colorFilledLine(): FilterPriceFormType['colorFilledLine'] {
        return this.priceFormGroup.get('colorFilledLine') as FilterPriceFormType['colorFilledLine'];
    }

    public get colorButtons(): FilterPriceFormType['colorButtons'] {
        return this.priceFormGroup.get('colorButtons') as FilterPriceFormType['colorButtons'];
    }

    protected readonly EFontStyle = EFontStyle;
    protected readonly ERadioGroupType = ERadioGroupType;
    protected readonly inputBorderEnum = InputBorderEnum;
    protected readonly CatalogCategoriesView = CatalogCategoriesView;
}
